import { isAxiosError } from 'axios';

interface ErrorResponseDetailObject {
  msg?: string;
  message?: string;
  type?: string;
}

interface ErrorResponseData {
  detail?: string | ErrorResponseDetailObject | ErrorResponseDetailObject[];
}

const detailObjectMessage = (detail: ErrorResponseDetailObject): string | undefined => {
  const msg = detail.msg ?? detail.message;
  if (typeof msg !== 'string') return undefined;
  const trimmed = msg.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

/**
 * Extract a user-facing string from an axios error's response body.
 *
 * Handles the FastAPI shapes we see across the API:
 *   1. `{ detail: "Maximum 100 domains allowed" }`              — string
 *   2. `{ detail: { msg: "...", message: "..." } }`             — validator-style object
 *   3. `{ detail: [{ msg: "...", loc: [...] }, ...] }`          — 422 request-validation array
 *
 * Returns `undefined` when no usable string is present so callers can fall
 * back to a localized default rather than leak `[object Object]` or `""`
 * into the UI. A bare transport error (no response body) returns `undefined`,
 * so callers keep their friendly generic message instead of surfacing
 * "Network Error".
 */
export function extractAxiosErrorDetail(err: unknown): string | undefined {
  if (!isAxiosError(err)) return undefined;

  const data = err.response?.data as ErrorResponseData | undefined;
  const detail = data?.detail;

  if (typeof detail === 'string') {
    const trimmed = detail.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  if (Array.isArray(detail)) {
    for (const entry of detail) {
      if (entry && typeof entry === 'object') {
        const msg = detailObjectMessage(entry);
        if (msg) return msg;
      }
    }
    return undefined;
  }

  if (detail && typeof detail === 'object') {
    return detailObjectMessage(detail);
  }

  return undefined;
}

/**
 * Extract the machine-readable `detail.type` discriminator from an axios error.
 *
 * Endpoints with structured errors return `{ detail: { type, msg } }`. Mapping
 * on `type` is stable across backend wording / casing / localization changes,
 * unlike matching the human-readable `msg`.
 *
 * Returns `undefined` for the plain-string `detail` shape (session-dependency
 * 404s and most other endpoints stay strings) so callers can fall back to
 * status-code-only handling.
 */
export function extractAxiosErrorType(err: unknown): string | undefined {
  if (!isAxiosError(err)) return undefined;

  const data = err.response?.data as ErrorResponseData | undefined;
  const detail = data?.detail;

  if (
    detail &&
    typeof detail === 'object' &&
    !Array.isArray(detail) &&
    typeof detail.type === 'string'
  ) {
    const trimmed = detail.type.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  return undefined;
}

/**
 * A PHI-safe summary of an error for logging — the HTTP status plus the error's own
 * message string, never the axios error object itself.
 *
 * The logger forwards its context to Datadog (`sendToDatadog`) without applying pino's
 * `redact` config, and an axios error carries `config.data` (the request body) and
 * `response.data` (the response body). For Scribe requests those bodies hold PHI —
 * transcript chunks, clinical orders, generated notes, and nudge card views (which embed
 * verbatim transcript quotes). Logging the raw error would ship that PHI to Datadog.
 * `error.message` ("Request failed with status code 500") and the status are safe.
 */
export function summarizeErrorForLog(err: unknown): {
  status?: number;
  message: string;
} {
  const status = isAxiosError(err) ? err.response?.status : undefined;
  const message = err instanceof Error ? err.message : String(err);
  return { status, message };
}
